import React, { useState, useEffect, useCallback ,useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Eye, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  XCircle,
  Building2,
  Calendar,
  Loader2,
  Target,
  Users,
  Shield,
  Zap,
  FlaskConical,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { dbService, IntroductionRequest, CDMOApplication, supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { getPrimaryCapabilityLabel } from '../../constants';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface WarmRequestWithCDMO extends IntroductionRequest {
  cdmo_applications?: CDMOApplication;
}

interface DashboardStats {
  accepted: number;
  pending: number;
  declined: number;
  total: number;
  onDataUpdate?: () => void; // Callback to notify parent of data updates
}

type FilterStatus = 'all' | 'accepted' | 'pending' | 'declined';

interface WarmRequestsTabProps {
  stats: DashboardStats;
  onStartConversation?: (introductionRequestId: string) => void;
  onDataUpdate?: () => void;
  /** Deep-link target (e.g. from an email's ?requestId= link) — scrolls/highlights the matching request once loaded. */
  highlightRequestId?: string | null;
}

const ITEMS_PER_PAGE = 12;

const WarmRequestsTab: React.FC<WarmRequestsTabProps> = ({ stats, onStartConversation, onDataUpdate, highlightRequestId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<WarmRequestWithCDMO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<WarmRequestWithCDMO | null>(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const subscriptionRef = useRef<RealtimeChannel | null>(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  
  // Expandable capabilities state
  const [expandedCapabilities, setExpandedCapabilities] = useState<Set<string>>(new Set());

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // This function already includes CDMO data via join
      const userRequests = await dbService.getUserIntroductionRequests(user!.id);
      console.log('Loaded requests:', userRequests);
      
      setRequests(userRequests);
      // Reset to first page when requests change
      setCurrentPage(1);
    } catch (err) {
      console.error('Error loading requests:', err);
      setError('Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadRequests();
    }
  }, [user, loadRequests]);

  // Real-time subscription for introduction request updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('startup-introduction-requests')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'introduction_requests',
          filter: `requester_user_id=eq.${user.id}`,
        },
        (payload) => {
          // Publication omits some columns for privacy; merge so joins / local fields are preserved
          const patch = payload.new as Partial<IntroductionRequest> & { id?: string };
          setRequests((prev) =>
            prev.map((req) => (req.id === patch.id ? { ...req, ...patch } : req))
          );
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Handle responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery]);

  const filteredRequests = requests.filter(request => {
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesSearch = !searchQuery || 
      request.cdmo_applications?.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.cdmo_applications?.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

  // Deep-link support: jump to the page containing the target request (list is
  // paginated, unlike the CDMO dashboard's equivalent), then scroll to and briefly
  // highlight it. Arrives via ?requestId=<id> on email links (e.g. the accept-request
  // notification's "Go to your Dashboard" link).
  useEffect(() => {
    if (!highlightRequestId || loading) return;

    const targetIndex = filteredRequests.findIndex(r => r.id === highlightRequestId);
    if (targetIndex === -1) return; // not found (filtered out, or belongs to another user)

    const targetPage = Math.floor(targetIndex / ITEMS_PER_PAGE) + 1;
    if (targetPage !== currentPage) {
      setCurrentPage(targetPage);
      return; // effect re-runs once currentPage updates and this page's rows render
    }

    const el = document.getElementById(`intro-request-${highlightRequestId}`);
    if (el) {
      el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-electric-blue');
      const timeout = setTimeout(() => el.classList.remove('ring-2', 'ring-electric-blue'), 3000);
      return () => clearTimeout(timeout);
    }
  }, [highlightRequestId, loading, filteredRequests, currentPage]);

  const handleViewDetails = (request: WarmRequestWithCDMO) => {
    console.log('View details for request:', request);
    setSelectedRequest(request);
    setShowDetailsPanel(true);
  };

  const handleStartConversation = (request: WarmRequestWithCDMO) => {
    if (onStartConversation) {
      onStartConversation(request.id!);
    }
  };
  const handleGiveFeedback = (requestId?: string) => {
    if (!requestId) {
      console.warn('handleGiveFeedback called without request id');
      return;
    }
    // requestId here is the introduction request id
    navigate(`/review/${requestId}/startup`);
  };

  const toggleCapabilities = (requestId: string) => {
    setExpandedCapabilities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const renderCapabilities = (request: WarmRequestWithCDMO) => {
    if (!request.cdmo_applications?.primary_capabilities || request.cdmo_applications.primary_capabilities.length === 0) {
      return null;
    }

    const capabilities = request.cdmo_applications.primary_capabilities;
    const isExpanded = expandedCapabilities.has(request.id!);
    const showExpandButton = capabilities.length > 5;
    const visibleCapabilities = isExpanded ? capabilities : capabilities.slice(0, 5);

    return (
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <Target className="w-4 h-4 text-electric-blue mr-2" />
          <span className="text-sm font-medium text-charcoal">Capabilities</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {visibleCapabilities.map((capability, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-electric-blue/10 text-electric-blue text-xs font-medium rounded-lg border border-electric-blue/20"
            >
              {getPrimaryCapabilityLabel(capability)}
            </span>
          ))}
          {showExpandButton && (
            <button
              onClick={() => toggleCapabilities(request.id!)}
              className="px-3 py-1 bg-slate-gray/10 text-slate-gray text-xs font-medium rounded-lg border border-slate-gray/20 hover:bg-slate-gray/20 transition-colors flex items-center"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3 h-3 mr-1" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3 mr-1" />
                  +{capabilities.length - 5} More
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };


   // Real-time subscription for introduction request updates
  useEffect(() => {
    if (!user?.id) return;

    // Cleanup existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    // Create real-time subscription for introduction requests
    const channelName = `warm-requests-tab-${user.id}-${Date.now()}`;
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'introduction_requests',
          filter: `requester_user_id=eq.${user.id}` // Only listen to this user's requests
        },
        async () => {

          try {
            // Reload requests to update UI
            await loadRequests();

            // Show update notification
            setShowUpdateNotification(true);
            setTimeout(() => setShowUpdateNotification(false), 3000);

            // Notify parent component to update stats
            if (onDataUpdate) {
              onDataUpdate();
            }
          } catch (error) {
            console.error('❌ Error updating WarmRequestsTab data:', error);
          }
        }
      )
      .subscribe();

    subscriptionRef.current = subscription;

    // Cleanup on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [user?.id, loadRequests, onDataUpdate]);



  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      
      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, start + maxVisiblePages - 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }
      
      return pages;
    };

    return (
      <div className="flex items-center justify-between mt-8">
        <div className="text-sm text-slate-gray">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredRequests.length)} of {filteredRequests.length} requests
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Previous Button */}
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-slate-gray/20 text-slate-gray hover:bg-slate-gray/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {/* Page Numbers */}
          <div className="flex items-center space-x-1">
            {getPageNumbers().map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-electric-blue text-white'
                    : 'text-slate-gray hover:bg-slate-gray/10'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-slate-gray/20 text-slate-gray hover:bg-slate-gray/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-lime-green" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'declined':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-slate-gray" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-lime-green/10 text-lime-green border-lime-green/20';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'declined':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-slate-gray/10 text-slate-gray border-slate-gray/20';
    }
  };

  const getTimelineEvents = (request: WarmRequestWithCDMO) => {
    const events: Array<{
      date: string | undefined;
      title: string;
      description: string;
      icon: React.ReactNode;
      status: 'completed' | 'pending';
    }> = [
      {
        date: request.created_at,
        title: 'Request Sent',
        description: 'Introduction request submitted to CDMO',
        icon: <MessageSquare className="w-4 h-4 text-electric-blue" />,
        status: 'completed'
      }
    ];

    if (request.status === 'accepted') {
      events.push({
        date: request.created_at, // Using created_at since updated_at doesn't exist in interface
        title: 'Request Accepted',
        description: 'CDMO has accepted your introduction request',
        icon: <CheckCircle className="w-4 h-4 text-lime-green" />,
        status: 'completed'
      });
    } else if (request.status === 'declined') {
      events.push({
        date: request.created_at, // Using created_at since updated_at doesn't exist in interface
        title: 'Request Declined',
        description: 'CDMO has declined your introduction request',
        icon: <XCircle className="w-4 h-4 text-red-600" />,
        status: 'completed'
      });
    } else if (request.status === 'pending') {
      events.push({
        date: undefined,
        title: 'Awaiting Response',
        description: 'Waiting for CDMO to review your request',
        icon: <Clock className="w-4 h-4 text-yellow-600" />,
        status: 'pending'
      });
    }

    return events;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-electric-blue animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadRequests}
          className="px-4 py-2 bg-electric-blue text-white rounded-lg hover:bg-electric-blue/90 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Summary Cards - Based on image */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-lime-green/10 rounded-2xl p-6 shadow-lg border border-lime-green/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-gray mb-1">Accepted</p>
              <p className="text-3xl font-bold text-lime-green">{stats.accepted}</p>
            </div>
            <div className="w-12 h-12 bg-lime-green/20 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-lime-green" />
            </div>
          </div>
        </div>

        <div className="bg-yellow-100 rounded-2xl p-6 shadow-lg border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-gray mb-1">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-red-100 rounded-2xl p-6 shadow-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-gray mb-1">Declined</p>
              <p className="text-3xl font-bold text-red-600">{stats.declined}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </motion.div>

     <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Filter Bar - Left side with counts */}
          <div className="flex flex-wrap gap-2">
            {([
              { status: 'all' as FilterStatus, label: 'All', count: stats.total },
              { status: 'accepted' as FilterStatus, label: 'Accepted', count: stats.accepted },
              { status: 'pending' as FilterStatus, label: 'Pending', count: stats.pending },
              { status: 'declined' as FilterStatus, label: 'Declined', count: stats.declined }
            ]).map((filter) => (
              <button
                key={filter.status}
                onClick={() => setStatusFilter(filter.status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  statusFilter === filter.status
                    ? 'bg-electric-blue text-white'
                    : 'bg-slate-gray/10 text-slate-gray hover:bg-slate-gray/20'
                }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>

          {/* Search - Right side on desktop, full width on mobile */}
          <div className="relative w-full lg:w-80 lg:ml-auto min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-gray flex-shrink-0" />
            <input
              type="text"
              placeholder="Search company or contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-gray/30 rounded-lg 
                         focus:ring-2 focus:ring-electric-blue focus:border-electric-blue 
                         transition-all duration-300 text-sm min-w-0 overflow-hidden
                         placeholder:text-slate-400 placeholder:truncate"
            />
          </div>
        </div>
      </div>

      {/* CDMO Cards Grid - Based on image with pagination */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {paginatedRequests.length === 0 ? (
          <div className="lg:col-span-2 xl:col-span-3 text-center py-12">
            <Building2 className="w-16 h-16 text-slate-gray/50 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-charcoal mb-2">No Requests Found</h3>
            <p className="text-slate-gray">
              {requests.length === 0 
                ? "You haven't made any introduction requests yet."
                : "No requests match your current filters."}
            </p>
          </div>
        ) : (
          paginatedRequests.map((request, index) => (
            <motion.div
              key={request.id}
              id={`intro-request-${request.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden transition-shadow"
            >
              {request.cdmo_applications && (
                <div className="p-6">
                  {/* Header with company info and status */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 shrink-0 bg-gradient-primary rounded-xl flex items-center justify-center mr-4 shadow-lg">
                        <Building2 className="w-6 h-6 shrink-0 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg font-bold text-charcoal mb-1">{request.cdmo_applications.company_name}</h3>
                        <p className="text-sm text-slate-gray">{request.cdmo_applications.contact_name}</p>
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <div className={`px-3 py-1 rounded-lg text-xs font-bold ${
                      request.status === 'accepted' 
                        ? 'bg-lime-green/10 text-lime-green border border-lime-green/20' 
                        : request.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                        : request.status === 'declined'
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }`}>
                      {request.status === 'accepted' ? 'Accepted' :
                       request.status === 'pending' ? 'Pending' :
                       request.status === 'declined' ? 'Declined' : request.status}
                    </div>
                  </div>

                  {/* Expandable Capabilities */}
                  {renderCapabilities(request)}

                  {/* Target Clients */}
                  {request.cdmo_applications.target_client_types && request.cdmo_applications.target_client_types.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <Users className="w-4 h-4 text-soft-teal mr-2" />
                        <span className="text-sm font-medium text-charcoal">Target Clients</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {request.cdmo_applications.target_client_types.slice(0, 2).map((clientType, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-soft-teal/10 text-soft-teal text-xs font-medium rounded-lg border border-soft-teal/20"
                          >
                            {clientType}
                          </span>
                        ))}
                        {request.cdmo_applications.target_client_types.length > 2 && (
                          <span className="px-3 py-1 bg-slate-gray/10 text-slate-gray text-xs font-medium rounded-lg">
                            +{request.cdmo_applications.target_client_types.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Timeline Info */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-slate-gray mr-2" />
                      <div>
                        <p className="text-xs text-slate-gray">Request Date</p>
                        <p className="text-sm font-medium text-charcoal">
                          {new Date(request.created_at || '').toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-slate-gray mr-2" />
                      <div>
                        <p className="text-xs text-slate-gray">Last Activity</p>
                        <p className="text-sm font-medium text-charcoal">
                          {new Date(request.created_at || '').toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleViewDetails(request)}
                      className="flex-1 px-4 py-2 bg-slate-gray/10 text-slate-gray rounded-lg hover:bg-slate-gray/20 transition-colors flex items-center justify-center"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </button>
                    
                    {request.status === 'accepted' && (
                      <button
                        onClick={() => handleStartConversation(request)}
                        className="flex-1 px-4 py-2 bg-electric-blue text-white rounded-lg hover:bg-electric-blue/90 transition-colors flex items-center justify-center"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Start Conversation
                      </button>
                    ) }
                    

                    {request.status === 'pending' && (
                      <button
                        className="flex-1 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg flex items-center justify-center cursor-not-allowed"
                        disabled
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Awaiting Response
                      </button>
                    )}
                  </div>
                  <div className="mt-3 w-full">
                    {request.status === 'accepted' && (
                      <button
                        onClick={() => handleGiveFeedback(request.id)}
                        className="w-full px-4 py-3 bg-gradient-to-r from-lime-green to-soft-teal text-white rounded-lg flex items-center justify-center"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Give Feedback
                      </button>
                    )}
                  </div>
                  
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Enhanced Details Panel - Responsive Sidebar/Modal */}
      <AnimatePresence>
        {showDetailsPanel && selectedRequest && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              onClick={() => setShowDetailsPanel(false)}
            />
            
            {/* Desktop: Right Sidebar, Mobile: Bottom Sheet */}
            <motion.div
              initial={{ 
                x: !isMobile ? '100%' : 0,
                y: isMobile ? '100%' : 0
              }}
              animate={{ 
                x: 0,
                y: 0
              }}
              exit={{ 
                x: !isMobile ? '100%' : 0,
                y: isMobile ? '100%' : 0
              }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`fixed z-50 overflow-y-auto bg-white shadow-2xl
                ${!isMobile 
                  ? 'right-0 top-16 h-[calc(100vh-4rem)] w-full max-w-2xl' 
                  : 'bottom-0 left-0 right-0 h-[85vh] max-h-[85vh] rounded-t-3xl'
                }`}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-gray/20">
                  <div>
                    <h2 className="text-2xl font-bold text-charcoal">
                      {selectedRequest.cdmo_applications?.company_name || 'CDMO Details'}
                    </h2>
                    <p className="text-slate-gray mt-1">Introduction Request Details</p>
                  </div>
                  <button
                    onClick={() => setShowDetailsPanel(false)}
                    className="w-8 h-8 rounded-lg bg-slate-gray/10 hover:bg-slate-gray/20 flex items-center justify-center transition-colors"
                  >
                    ✕
                  </button>
                </div>

                {/* Request Status */}
                <div className="mb-8">
                  <div className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(selectedRequest.status || 'pending')}`}>
                    {getStatusIcon(selectedRequest.status || 'pending')}
                    <span className="ml-2">
                      {selectedRequest.status === 'accepted' ? 'Request Accepted' :
                       selectedRequest.status === 'pending' ? 'Request Pending' :
                       selectedRequest.status === 'declined' ? 'Request Declined' : 'Unknown Status'}
                    </span>
                  </div>
                </div>

                {/* CDMO Details */}
                {selectedRequest.cdmo_applications ? (
                  <div className="space-y-8">
                    {/* Full Capabilities List in Details Panel */}
                    {selectedRequest.cdmo_applications.primary_capabilities && selectedRequest.cdmo_applications.primary_capabilities.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center">
                          <FlaskConical className="w-5 h-5 text-electric-blue mr-2" />
                          All Capabilities
                        </h3>
                        <div className="bg-slate-gray/5 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {selectedRequest.cdmo_applications.primary_capabilities.map((capability, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-electric-blue/10 text-electric-blue text-sm font-medium rounded-lg border border-electric-blue/20"
                              >
                                {getPrimaryCapabilityLabel(capability)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Target Clients */}
                    {selectedRequest.cdmo_applications.target_client_types && selectedRequest.cdmo_applications.target_client_types.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center">
                          <Users className="w-5 h-5 text-soft-teal mr-2" />
                          Target Clients
                        </h3>
                        <div className="bg-slate-gray/5 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {selectedRequest.cdmo_applications.target_client_types.map((clientType, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-soft-teal/10 text-soft-teal text-sm font-medium rounded-lg border border-soft-teal/20"
                              >
                                {clientType}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Batch Sizes */}
                    {selectedRequest.cdmo_applications.typical_batch_sizes && selectedRequest.cdmo_applications.typical_batch_sizes.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center">
                          <Target className="w-5 h-5 text-lime-green mr-2" />
                          Batch Sizes
                        </h3>
                        <div className="bg-slate-gray/5 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {selectedRequest.cdmo_applications.typical_batch_sizes.map((batchSize, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-lime-green/10 text-lime-green text-sm font-medium rounded-lg border border-lime-green/20"
                              >
                                {batchSize}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Certifications */}
                    {selectedRequest.cdmo_applications.gmp_certifications && selectedRequest.cdmo_applications.gmp_certifications.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center">
                          <Shield className="w-5 h-5 text-lime-green mr-2" />
                          GMP Certifications
                        </h3>
                        <div className="bg-slate-gray/5 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {selectedRequest.cdmo_applications.gmp_certifications.map((cert, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-lime-green/10 text-lime-green text-sm font-medium rounded-lg border border-lime-green/20"
                              >
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Engagement Scales */}
                    {selectedRequest.cdmo_applications.engagement_scales && selectedRequest.cdmo_applications.engagement_scales.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center">
                          <Zap className="w-5 h-5 text-gradient-pink-violet-start mr-2" />
                          Development Stages
                        </h3>
                        <div className="bg-slate-gray/5 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {selectedRequest.cdmo_applications.engagement_scales.map((scale, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-gradient-pink-violet-start/10 text-gradient-pink-violet-start text-sm font-medium rounded-lg border border-gradient-pink-violet-start/20"
                              >
                                {scale}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    {selectedRequest.cdmo_applications.description && (
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-4">Description</h3>
                        <div className="bg-slate-gray/5 rounded-xl p-4">
                          <p className="text-slate-gray leading-relaxed">{selectedRequest.cdmo_applications.description}</p>
                        </div>
                      </div>
                    )}

                    {/* Timeline */}
                    <div>
                      <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center">
                        <Calendar className="w-5 h-5 text-electric-blue mr-2" />
                        Timeline
                      </h3>
                      <div className="space-y-4">
                        {getTimelineEvents(selectedRequest).map((event, index) => (
                          <div key={index} className="flex items-start space-x-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              event.status === 'completed' 
                                ? 'bg-electric-blue/10' 
                                : 'bg-yellow-100'
                            }`}>
                              {event.icon}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-charcoal">{event.title}</h4>
                              <p className="text-sm text-slate-gray mt-1">{event.description}</p>
                              {event.date && (
                                <p className="text-xs text-slate-gray mt-1">
                                  {new Date(event.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                            {index < getTimelineEvents(selectedRequest).length - 1 && (
                              <div className="w-px h-8 bg-slate-gray/20 ml-4" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-slate-gray/50 mx-auto mb-4" />
                    <p className="text-slate-gray">CDMO details not available</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 mt-8 mb-6 pt-6 border-t border-slate-gray/20">
                  <button
                    onClick={() => setShowDetailsPanel(false)}
                    className="flex-1 px-4 py-3 bg-slate-gray/10 text-slate-gray rounded-lg hover:bg-slate-gray/20 transition-colors"
                  >
                    Close
                  </button>
                  
                  {selectedRequest.status === 'accepted' && (
                    <button
                      onClick={() => {
                        handleStartConversation(selectedRequest);
                        setShowDetailsPanel(false);
                      }}
                      className="flex-1 px-4 py-3 bg-electric-blue text-white rounded-lg hover:bg-electric-blue/90 transition-colors flex items-center justify-center"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Start Conversation
                    </button>
                  )}

                  {selectedRequest.status === 'pending' && (
                    <button
                      className="flex-1 px-4 py-3 bg-yellow-100 text-yellow-700 rounded-lg flex items-center justify-center cursor-not-allowed"
                      disabled
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Awaiting Response
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WarmRequestsTab;
